// Cluster REGELN (G4, H1–H5) — reine, React-freie Logik hinter dem
// Regel-Fenster: Vorbelegung aus einer Mail, Klartext einer Regel, Prüfung
// eines Entwurfs.
//
// Bewusst DOM- und React-frei, damit die Regeln prüfbar sind, ohne das Fenster
// zu rendern (mailRegel.test.ts).

import { betreffOhneAntwortPraefix } from "../../utils/betreffPraefix";
import type {
    MailVorgangRegelDto,
    RegelAktion,
    RegelBedingung,
    RegelFeld,
    RegelVerknuepfung,
} from "../../services/mailVorgangRegelService";
import { FELD_LABEL } from "../../services/mailVorgangRegelService";
import type { MailLinkDto } from "../../services/mailLinkService";

/** Obergrenze je Regel — Spiegel von MAX_BEDINGUNGEN_PRO_REGEL im Backend. */
export const MAX_BEDINGUNGEN = 8;

/** Der Vorgang, an den eine Regel zuweist (nur für Anzeige und Prüfung). */
export type RegelZielVorgang = { id: number; label: string };

/** Der Entwurf, den das Formular hält — 1:1 die Eingabe des Endpoints plus Anzeige-Ziel. */
export interface RegelEntwurf {
    name: string;
    bedingungen: RegelBedingung[];
    verknuepfung: RegelVerknuepfung;
    /** Leer = in allen beobachteten Postfächern. */
    mailbox: string | null;
    aktion: RegelAktion;
    /** Nur bei `aktion === "zuweisen"`. */
    ziel: RegelZielVorgang | null;
    aktionVorgangsart: string | null;
    aktionBetreffQuelle: "mail" | "fest";
    aktionBetreff: string;
    aktionKontaktQuelle: "erkannt" | "fest" | "ohne";
    aktionKontaktId: number | null;
    aktionKontaktLabel: string | null;
    aktionHauptverantwortlich: string[];
}

/** Eine leere Bedingung der gewünschten Art. */
export function leereBedingung(feld: RegelFeld): RegelBedingung {
    return feld === "zeitraum" ? { feld: "zeitraum", von: null, bis: null } : { feld, wert: "" };
}

/** Ein leerer Entwurf — die konservative Aktion „zuweisen" ist voreingestellt. */
export function leererEntwurf(): RegelEntwurf {
    return {
        name: "",
        bedingungen: [leereBedingung("absender")],
        verknuepfung: "und",
        mailbox: null,
        aktion: "zuweisen",
        ziel: null,
        aktionVorgangsart: null,
        aktionBetreffQuelle: "mail",
        aktionBetreff: "",
        aktionKontaktQuelle: "erkannt",
        aktionKontaktId: null,
        aktionKontaktLabel: null,
        aktionHauptverantwortlich: [],
    };
}

/** Betreff ohne Meta-Präfix (E04: das Courté-Schema bleibt stehen). */
export function betreffOhneMetaPraefix(subject: string | null | undefined): string {
    return betreffOhneAntwortPraefix(subject);
}

/**
 * Vorbelegung aus einer angeklickten Mail: Absender als erste Bedingung,
 * Postfach gesetzt. Der bereinigte Betreff wird NICHT automatisch zur
 * Bedingung — er wäre in aller Regel zu speziell; das Fenster bietet ihn als
 * anklickbaren Vorschlag an.
 */
export function vorbelegungAusMail(quelle: {
    senderEmail?: string | null;
    subject?: string | null;
    mailbox?: string | null;
    vorgang?: RegelZielVorgang | null;
}): RegelEntwurf {
    const entwurf = leererEntwurf();
    const adresse = String(quelle?.senderEmail ?? "")
        .trim()
        .toLowerCase();
    if (adresse && adresse.includes("@")) {
        entwurf.bedingungen = [{ feld: "absender", wert: adresse }];
    }
    const mailbox = String(quelle?.mailbox ?? "")
        .trim()
        .toLowerCase();
    if (mailbox) entwurf.mailbox = mailbox;
    if (quelle?.vorgang) {
        entwurf.aktion = "zuweisen";
        entwurf.ziel = quelle.vorgang;
    }
    return entwurf;
}

/** Betreff-Vorschlag zu einer Mail — leer, wenn nach dem Bereinigen nichts bleibt. */
export function betreffVorschlag(subject: string | null | undefined): string {
    return betreffOhneMetaPraefix(subject);
}

/** Eine gespeicherte Regel zurück in einen bearbeitbaren Entwurf. */
export function entwurfAusRegel(
    regel: MailVorgangRegelDto,
    zielLabel?: string | null,
): RegelEntwurf {
    const bedingungen = bedingungenVonRegel(regel);
    return {
        name: regel.name ?? "",
        bedingungen: bedingungen.length ? bedingungen : [leereBedingung("absender")],
        verknuepfung: regel.verknuepfung ?? "und",
        mailbox: regel.mailbox ?? null,
        aktion: regel.aktion ?? "zuweisen",
        ziel: regel.vorgangId
            ? { id: regel.vorgangId, label: zielLabel || `Vorgang #${regel.vorgangId}` }
            : null,
        aktionVorgangsart: regel.aktionVorgangsart ?? null,
        aktionBetreffQuelle: regel.aktionBetreffQuelle === "fest" ? "fest" : "mail",
        aktionBetreff: regel.aktionBetreff ?? "",
        aktionKontaktQuelle:
            regel.aktionKontaktQuelle === "fest"
                ? "fest"
                : regel.aktionKontaktQuelle === "ohne"
                  ? "ohne"
                  : "erkannt",
        aktionKontaktId: regel.aktionKontaktId ?? null,
        aktionKontaktLabel: regel.aktionKontaktId ? `Kontakt #${regel.aktionKontaktId}` : null,
        aktionHauptverantwortlich: regel.aktionHauptverantwortlich ?? [],
    };
}

/**
 * Bedingungen einer gespeicherten Regel — mit Bestandsschutz: eine Regel von
 * vor H1 trägt nur `kriterium`/`wert`.
 */
export function bedingungenVonRegel(regel: MailVorgangRegelDto): RegelBedingung[] {
    const liste = Array.isArray(regel?.bedingungen) ? regel.bedingungen : [];
    if (liste.length) return liste;
    if (regel?.kriterium && regel?.wert) return [{ feld: regel.kriterium, wert: regel.wert }];
    return [];
}

/** Deutsches Datum aus 'YYYY-MM-DD' (leer bleibt leer). */
function datumKlartext(iso: string | null): string {
    if (!iso) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** Eine einzelne Bedingung als Satzteil. */
export function bedingungKlartext(b: RegelBedingung): string {
    if (b.feld === "zeitraum") {
        const von = datumKlartext(b.von);
        const bis = datumKlartext(b.bis);
        if (von && bis) return `${FELD_LABEL.zeitraum} ${von} und ${bis}`;
        if (von) return `Empfangen ab ${von}`;
        if (bis) return `Empfangen bis ${bis}`;
        return "Empfangen (Zeitraum offen)";
    }
    return `${FELD_LABEL[b.feld]} ${b.wert}`;
}

/**
 * Die ganze Regel als ein deutscher Satz — Platzhalter des Namensfelds und
 * Zeile in der Verwaltungsliste.
 */
export function regelKlartext(
    regel: Pick<
        RegelEntwurf,
        | "bedingungen"
        | "verknuepfung"
        | "mailbox"
        | "aktion"
        | "ziel"
        | "aktionVorgangsart"
    >,
): string {
    const teile = (regel.bedingungen ?? []).filter(istBedingungGefuellt).map(bedingungKlartext);
    const wenn = teile.length
        ? `Wenn ${teile.join(regel.verknuepfung === "oder" ? " ODER " : " UND ")}`
        : "Wenn (noch keine Bedingung)";

    const dann =
        regel.aktion === "neuer_vorgang"
            ? `neuen Vorgang${regel.aktionVorgangsart ? ` (${regel.aktionVorgangsart})` : ""} anlegen`
            : regel.ziel
              ? `zuweisen an „${regel.ziel.label}"`
              : "zuweisen (kein Vorgang gewählt)";

    const wo = regel.mailbox ? `, nur in ${regel.mailbox}` : "";
    return `${wenn} → ${dann}${wo}`;
}

/** Klartext einer GESPEICHERTEN Regel (Liste). */
export function regelDtoKlartext(regel: MailVorgangRegelDto, zielLabel?: string | null): string {
    return regelKlartext({
        bedingungen: bedingungenVonRegel(regel),
        verknuepfung: regel.verknuepfung ?? "und",
        mailbox: regel.mailbox ?? null,
        aktion: regel.aktion ?? "zuweisen",
        ziel: regel.vorgangId
            ? { id: regel.vorgangId, label: zielLabel || `Vorgang #${regel.vorgangId}` }
            : null,
        aktionVorgangsart: regel.aktionVorgangsart ?? null,
    });
}

/** Trägt die Bedingung überhaupt einen Wert? */
export function istBedingungGefuellt(b: RegelBedingung): boolean {
    return b.feld === "zeitraum" ? !!(b.von || b.bis) : !!String(b.wert ?? "").trim();
}

/**
 * Prüft einen Entwurf. Rückgabe `null` = speicherbar, sonst die deutsche
 * Meldung, die über der Fußzeile erscheint.
 *
 * Bewusst dieselben Regeln wie im Backend — die Prüfung hier ist Komfort, die
 * verbindliche sitzt im Endpoint.
 */
export function validiereRegel(entwurf: RegelEntwurf): string | null {
    const gefuellt = (entwurf.bedingungen ?? []).filter(istBedingungGefuellt);
    if (!gefuellt.length) return "Die Regel braucht mindestens eine Bedingung.";
    if (gefuellt.length > MAX_BEDINGUNGEN) {
        return `Höchstens ${MAX_BEDINGUNGEN} Bedingungen je Regel.`;
    }

    if (entwurf.aktion === "zuweisen" && !entwurf.ziel?.id) {
        return "Bitte den Vorgang wählen, dem die Mails zugewiesen werden sollen.";
    }

    if (entwurf.aktion === "neuer_vorgang") {
        if (entwurf.aktionBetreffQuelle === "fest" && !entwurf.aktionBetreff.trim()) {
            return "Bitte den festen Betreff eintragen.";
        }
        if (entwurf.aktionKontaktQuelle === "fest" && !entwurf.aktionKontaktId) {
            return "Bitte den Kontakt wählen, an dem der neue Vorgang hängen soll.";
        }
    }
    return null;
}

/** Entwurf → Request-Body des Endpoints. */
export function entwurfAlsEingabe(entwurf: RegelEntwurf) {
    const bedingungen = (entwurf.bedingungen ?? []).filter(istBedingungGefuellt);
    return {
        name: entwurf.name.trim() || null,
        bedingungen,
        verknuepfung: entwurf.verknuepfung,
        mailbox: entwurf.mailbox || null,
        aktion: entwurf.aktion,
        vorgangId: entwurf.aktion === "zuweisen" ? (entwurf.ziel?.id ?? null) : null,
        aktionVorgangsart: entwurf.aktionVorgangsart || null,
        aktionBetreffQuelle: entwurf.aktionBetreffQuelle,
        aktionBetreff: entwurf.aktionBetreff.trim() || null,
        aktionKontaktQuelle: entwurf.aktionKontaktQuelle,
        aktionKontaktId: entwurf.aktionKontaktQuelle === "fest" ? entwurf.aktionKontaktId : null,
        aktionHauptverantwortlich: entwurf.aktionHauptverantwortlich.length
            ? entwurf.aktionHauptverantwortlich
            : null,
    };
}

/**
 * Absender der zugewiesenen Mails, jüngste zuerst, ohne Dubletten — die
 * Vorschlagsliste „Aus dem Schriftwechsel" im Regel-Fenster.
 *
 * Wanderte aus `MailRegelDialog.tsx` hierher, damit sie das Löschen jener Datei
 * überlebt.
 */
export function absenderVorschlaege(links: MailLinkDto[]): string[] {
    const sortiert = [...(links ?? [])].sort((a, b) => {
        const ta = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
        const tb = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
        return tb - ta;
    });
    const out: string[] = [];
    for (const l of sortiert) {
        const adresse = String(l.senderEmail ?? "")
            .trim()
            .toLowerCase();
        if (adresse && adresse.includes("@") && !out.includes(adresse)) out.push(adresse);
    }
    return out;
}
